<template>
  <div
    class="flex flex-col min-h-full"
    ref="myPresentations"
  >
    <public-announcement :is-fixed="true" />
    <invitation-notifications v-if="$store.state.currentUser.id" />
    <edu-yearly-notification v-if="$store.state.currentUser.id" />
    <div v-if="!isSearching">
      <template-panel
        v-if="showTemplatePanel && !$store.state.isEnableAddinPowerpoint"
        class="flex-col hidden px-4 pt-6 pb-4 md:flex md:px-10"
      />
    </div>
    <portal to="report-panel-destination">
      <ReportPanel />
    </portal>
    <div class="m-4 mt-6 md:px-6">
      <!-- Breadcrumb for normal user, hidden when user's searching -->
      <MyPresentationsBreadcrumbs
        v-if="!isEURegion"
        v-show="isMyPresentationsBreadcrumbsVisible"
        :second-level="breadcrumbsCategory"
        :n-level-array="breadcrumbsMultipleLevelArray"
        :isSecondLevelClickable="isSecondLevelClickable"
        :defaultTitle="breadcrumbsTitle"
        @clickSecondLevel="clickBreadcrumbsSecondLevel()"
        @clickNLevel="clickBreadcrumbsNLevel"
        :hasMultipleLevels="isSecondLevelClickable"
      />
      <!-- Breadcrumb just for EU user, is still shown when user's searching -->
      <MyPresentationsBreadcrumbs
        v-if="isEURegion"
        v-show="isMyPresentationsBreadcrumbsForEUVisible"
        :second-level="breadcrumbsCategory"
        :n-level-array="breadcrumbsMultipleLevelArray"
        :isSecondLevelClickable="isSecondLevelClickable"
        :defaultTitle="breadcrumbsTitle"
        @clickSecondLevel="clickBreadcrumbsSecondLevel()"
        @clickNLevel="clickBreadcrumbsNLevel"
        :hasMultipleLevels="isSecondLevelClickable"
      />
      <tool-box v-show="!empty" />
    </div>

    <div
      class="flex-grow"
      :class="{'flex items-center justify-center mr-4 ml-4 md:px-6':
        empty || presentationsAndFoldersLoading
      }"
    >
      <template v-if="presentationsAndFoldersLoading">
        <InlineLoader
          :additionalClasses="['-mt-8', {
            '-mt-16': $route.name === 'SharedWithMe'}]"
        />
      </template>

      <template v-else>
        <div class="flex items-center justify-center h-full" v-if="empty">
          <not-found v-if="searchValue" />
          <on-boarding-box
            v-if="!searchValue && !$route.params.folderId"
            :shared="$route.name === 'SharedWithMe'"
          />
          <div v-if="!searchValue && (!isRouteTeam) || (isRouteTeamFolderDetails && $route.params.folderId)">
            <empty :shared="$route.name === 'SharedWithMe'" />
            <tool-box
              class="flex justify-center w-full mt-4"
            />
          </div>
        </div>

        <main-table v-else class="pb-12" />
      </template>
    </div>
    <LoadingScreen v-if="isCreatingNewPresentation" />
    <TrialNotAvailableModal v-if="isShowTrialNotEligibleModal" @hide="hideTrialNotAvailableModal" />
    <EnjoyTrialModal v-if="isShowEnjoyTrialModal" @hide="isShowEnjoyTrialModal=false" />
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex'

// configs
import configs from '@/config'

// event bus
import EventBus from '@/eventBus'

// utils
import utils from '@/util'
import { preloadTinyMCE } from '@/utils/script.utils'

// constants
import constants from '@/constant'
import {
  canShowEnjoyTrialPopup, canShowTrialNotAvailablePopup, clearPopupSessionData,
} from '@/utils/trial-plan.utils'

// components
import InvitationNotifications from '@/components-v2/common/global-notification/InvitationNotifications.vue'
import EduYearlyNotification from '@/components-v2/common/global-notification/EduYearlyNotification.vue'
import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import TemplatePanel from '@/components-v2/common/template-library/TemplatePanel.vue'
import ReportPanel from '@/components-v2/presentation-report/ReportPanel.vue'
import NotFound from './NotFound.vue'
import MainTable from './main-table/MainTable.vue'
import MyPresentationsBreadcrumbs from '@/components-v2/common/page-breadcrumb/MyPresentationsBreadcrumbs.vue'
import ToolBox from './ToolBox.vue'
import Empty from '@/components-v2/my-presentations/Empty.vue'
import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'
import InlineLoader from '@/components-v2/common/loading/InlineLoader.vue'
import OnBoardingBox from '@/components-v2/my-presentations/OnBoardingBox.vue'
import EnjoyTrialModal from '@/components-v2/modals/trial/EnjoyTrialModal.vue'
import TrialNotAvailableModal from '@/components-v2/modals/trial/TrialNotAvailableModal.vue'

const { eventCategories } = constants
const WHITE_LABEL = 'white-label'

export default {
  components: {
    NotFound,
    MainTable,
    MyPresentationsBreadcrumbs,
    ToolBox,
    InvitationNotifications,
    EduYearlyNotification,
    TemplatePanel,
    ReportPanel,
    Empty,
    PublicAnnouncement,
    LoadingScreen,
    InlineLoader,
    OnBoardingBox,
    TrialNotAvailableModal,
    EnjoyTrialModal,
  },
  data() {
    return {
      domain: '',
      searchValue: '',
      presentationsAndFoldersLoading: true,
      showingGlobalNotification: true,
      notifications: [],
      isShowEarlyBirdModal: false,
      numberOfSameEmailDomainUsers: 0,
      suggestedTeams: [],
      isShowTrialNotEligibleModal: false,
      isShowEnjoyTrialModal: false,

    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
      folders: (state) => state.folders,
      hasOnboardingV3: (state) => state.indicators.hasOnboardingV3,
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      numberOfPresentationPages: (state) => state.numbers.numberOfPresentationPages,
      numberOfLoadedPresentationPages: (state) => state.numbers.numberOfLoadedPresentationPages,
      morePresentationsLoading: (state) => state.loaders.morePresentationsLoading,
      selectedFolderId: (state) => state.selections.selectedFolderId,
      presentations: (state) => state.presentations,
      isServerConnectable: (state) => state.indicators.isServerConnectable,
      selectedSortCriteria: (state) => state.selections.selectedSortCriteria,
      isCreatingNewPresentation: (state) => state.isCreatingNewPresentation,
      breadcrumbsMultipleLevelsArray: (state) => state.breadcrumbsMultipleLevelsArray,
      isEnableAddinPowerpoint: (state) => state.isEnableAddinPowerpoint,
    }),
    ...mapGetters(['selectedFolder']),
    ...mapGetters({
      selectedPresentationToGetReport: 'presentationReport/selectedPresentation',
    }),
    empty() {
      if (!this.presentations && !this.folders) {
        return true
      }
      if (this.isRouteTeamFolderDetails) {
        return !this.presentations.length
      }
      return !this.presentations.length && !this.folders.length
    },
    breadcrumbsCategory() {
      if (this.isRouteTeam || this.isRouteTeamFolderDetails) {
        return this.currentAccount.name
      }
      if (this.isRouteSharedWithMe || this.isShareRouteFolderDetails) {
        return this.$i18nPresenter.t('Shared with Me')
      }
      return this.$i18nPresenter.t('My Presentations')
    },
    breadcrumbsTitle() {
      if (this.isRouteSharedWithMe) {
        return this.$i18nPresenter.t('Shared with Me')
      }
      if (this.isRouteTeam) {
        return this.$i18nPresenter.t('Team Presentations')
      }
      return this.$store.getters.selectedFolder ? this.$store.getters.selectedFolder.folderName : this.$i18nPresenter.t('My Presentations')
    },
    breadcrumbsMultipleLevelArray() {
      if (this.isRouteTeamFolderDetails) {
        return this.selectedFolder ? [this.selectedFolder] : []
      }
      return (this.breadcrumbsMultipleLevelsArray && this.breadcrumbsMultipleLevelsArray.length)
        ? this.breadcrumbsMultipleLevelsArray : []
    },
    isClickableBreadcrumb() {
      if (this.selectedFolder) {
        return true
      }
      return false
    },
    isEURegion() {
      return configs.region === constants.euRegion
    },
    isSearching() {
      return this.$route.query.q
    },
    hasPresentations() {
      return this.presentations && this.presentations.length > 0
    },
    isTestingNewSearch() {
      if (this.$store.state.currentUser.id % 4 === 1) return true
      return false
    },
    showTemplatePanel() {
      if (this.isEnableAddinPowerpoint) return false
      if (this.$route.name === 'Presentations') return true
      if (this.$route.name === 'SharedWithMe' || this.$route.name === 'Team' || this.$route.name === 'TeamFolderDetail') return false
      if (this.currentUser.id) return false
      return true
    },
    isTeamViewer() {
      return this.currentUser && this.currentUser.accountRole
        && this.currentUser.accountRole === constants.workspace.VIEWER
        && this.$store.state.workingMode === constants.workspace.TEAM
    },
    isWhiteLabel() {
      return this.$store.state.plan.group === 'white-label'
    },
    isUserHasCreatedPresentation() {
      return this.currentUser && this.currentUser.personalInformation &&
        this.currentUser.personalInformation.hasCreatedPresentation
    },
    isPersonalWorkspace() {
      return this.$route.name === 'Presentations' && this.$store.state.workingMode === constants.workspace.PERSONAL
    },
    isRouteFolderDetails() {
      return this.$route.name === 'FolderDetail'
    },
    isShareRouteFolderDetails() {
      return this.$route.name === 'ShareFolderDetail'
    },
    isRouteTeamFolderDetails() {
      return this.$route.name === 'TeamFolderDetail'
    },
    isRouteTeam() {
      return this.$route.name === 'Team'
    },
    isRouteSharedWithMe() {
      return this.$route.name === 'SharedWithMe'
    },
    isMyPresentationsBreadcrumbsVisible() {
      return !this.isSearching &&
            (this.isRouteSharedWithMe || this.isRouteTeam || this.isRouteTeamFolderDetails ||
              this.$route.params.folderId || (!this.presentationsAndFoldersLoading && !this.empty)
            )
    },
    isMyPresentationsBreadcrumbsForEUVisible() {
      return this.isRouteSharedWithMe || this.$route.params.folderId ||
          (!this.presentationsAndFoldersLoading && !this.empty)
    },
    isSecondLevelClickable() {
      return this.breadcrumbsMultipleLevelArray && this.breadcrumbsMultipleLevelArray.length > 0
    },
  },

  watch: {
    'currentUser.whiteLabelLink': function handleWhiteLableLinkChange() {
      if (this.currentUser.plan === WHITE_LABEL) {
        this.domain = this.currentUser.whiteLabelLink
      }
    },
    'currentUser.accountId': async function onChangeAccountId(newAccountIdValue) {
      if (
        newAccountIdValue === null
        && (this.$route.name === 'Team' || this.$route.name === 'TeamMember' || this.$route.name === 'TeamFolderDetail')
      ) {
        this.$router.push('/apps/presentations')
      }

      const params = {
        fromWorkspace: true,
        accountId: newAccountIdValue,
      }
      if (this.$route.name === 'Team') await this.getTeamFoldersAction(params)
    },
    selectedSortCriteria() {
      this.setNumberOfLoadedPresentationPagesAction(1)
      this._getPresentations()
    },
    $route: function handleChangeRoute(newRoute) {
      if (newRoute.name === 'Presentations') {
        this.searchValue = this.$route.query.q || ''
      } else {
        this.searchValue = ''
      }
      if (newRoute.name === 'FolderDetail' || newRoute.name === 'TeamFolderDetail' || newRoute.name === 'ShareFolderDetail') {
        this.presentationsAndFoldersLoading = true
        this.setPresentationsAction([])
        this.setSelectedFolderIdAction(parseInt(newRoute.params.folderId, 10))
      } else {
        this.setSelectedFolderIdAction(null)
      }

      if (newRoute.name === 'SharedWithMe') {
        this.setFoldersAction([])
        this.setPresentationsAction([])
      }

      this.setNumberOfLoadedPresentationPagesAction(1)
      this._getPresentations()
    },
  },
  async mounted() {
    // scroll to top to see global notifications. Avoid heading overlap
    window.scrollTo({ top: 0, behavior: 'smooth' })

    this.domain = configs.domain

    this.searchValue = this.$route.query.q || ''

    this.setNumberOfLoadedPresentationPagesAction(1)
    window.addEventListener('scroll', this.handleMyPresentationsScroll)
    EventBus.$on('reload-presentations-and-folders', this._getPresentations)

    if (this.$route.params.folderId) {
      if (this.isFolderIdValid(this.$route.params.folderId)) {
        this.setSelectedFolderIdAction(parseInt(this.$route.params.folderId, 10))
      } else {
        this.$router.push({
          name: 'Presentations',
        })
      }
    }
    await this._getPresentations()
    preloadTinyMCE()
    this.checkToShowEarlyBirdModal()
    this.checkToShowTrialModal()
  },
  destroyed() {
    this.$store.state.openImportModal = false
    window.onscroll = null
    this.setSelectedFolderIdAction(null)
    EventBus.$off('reload-presentations-and-folders')
    window.removeEventListener('scroll', this.handleMyPresentationsScroll)
    this.unsubscribeReportAction()
    this.clearPresentationReportAction()
  },
  methods: {
    ...mapActions([
      'getPresentationsAction',
      'getSharedPresentationsAction',
      'setSelectedFolderIdAction',
      'getFoldersAction',
      'setFoldersAction',
      'getTeamFoldersAction',
      'setPresentationsAction',
      'setIsServerConnectableAction',
      'setNumberOfLoadedPresentationPagesAction',
      'setMorePresentationsLoadingAction',
      'setCurrentAccountAction',
      'setCurrentUserAction',
      'getSubFoldersAction',
      'getShareFoldersAction',
      'getShareSubFoldersAction',
      'setErrorOnInternalServerAction',
    ]),
    ...mapActions('presentationReport', ['clearPresentationReportAction', 'unsubscribeReportAction']),
    isFolderIdValid(folderId) {
      return !Number.isNaN(parseInt(folderId, 10))
    },
    async _getPresentations() {
      if (this.$route.name === 'Presentations' || this.isRouteFolderDetails) {
        await this.getPresentationsAndFolders()
      } else if (this.$route.name === 'SharedWithMe' || this.isShareRouteFolderDetails) {
        await this.getSharePresentationsAndFolders()
      } else if (this.$route.name === 'Team' || this.$route.name === 'TeamFolderDetail') {
        if (configs.region === constants.euRegion) {
          this.$router.push({ name: 'Presentations' })
        } else {
          await this.getTeamPresentationsAndFolders()
        }
      }
      this.syncWorkingMode()
    },
    syncWorkingMode() {
      // prevent user auto input link in url bar
      if (this.$route.name === 'Presentations' && this.$store.state.workingMode === constants.workspace.TEAM) {
        this.$store.state.workingMode = constants.workspace.PERSONAL
        window.localStorage.setItem(`${constants.workspace.WORKING_MODE}-${this.currentUser.id}`, constants.workspace.PERSONAL)
      } else if (this.$route.name === 'Team' && this.$store.state.workingMode === constants.workspace.PERSONAL) {
        this.$store.state.workingMode = constants.workspace.TEAM
        window.localStorage.setItem(`${constants.workspace.WORKING_MODE}-${this.currentUser.id}`, constants.workspace.TEAM)
      }
    },
    async getSharedPresentations() {
      if (!this.isServerConnectable) return

      const params = {
        page: this.numberOfLoadedPresentationPages,
        sortOrder: this.selectedSortCriteria.sortOrder,
        sortColumn: this.selectedSortCriteria.sortColumn,
      }

      if (this.searchValue.trim()) {
        params.textSearch = this.searchValue.trim()
      }

      // show loader
      if (this.numberOfLoadedPresentationPages === 1) {
        this.presentationsAndFoldersLoading = true
      } else {
        this.setMorePresentationsLoadingAction(true)
      }

      try {
        await this.getSharedPresentationsAction(params)
      } catch (error) {
        // this.setIsServerConnectableAction(false)
        utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Shared Presentations > Load > Failed')
        if (error.status === 403) {
          this.$router.push({ name: 'SharedWithMe' })
          this.setErrorOnInternalServerAction(this.$i18nPresenter.t('Folder not found!'))
        }
        throw error
      } finally {
        this.presentationsAndFoldersLoading = false
        this.setMorePresentationsLoadingAction(false)
      }

      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Shared Presentations > Load > Succeeded')

      if (this.currentUser.plan === WHITE_LABEL) {
        this.domain = this.currentUser.whiteLabelLink
      }
    },

    async getPresentationsAndFolders() {
      if (!this.isServerConnectable) return
      const params = {
        page: this.numberOfLoadedPresentationPages,
        sortOrder: this.selectedSortCriteria.sortOrder,
        sortColumn: this.selectedSortCriteria.sortColumn,
        includeShared: true,
      }

      if (this.searchValue.trim()) {
        params.textSearch = this.searchValue.trim()
      } else {
        params.folderId = this.selectedFolderId || null
      }

      // show loader
      if (this.numberOfLoadedPresentationPages === 1) {
        this.presentationsAndFoldersLoading = true
      } else {
        this.setMorePresentationsLoadingAction(true)
      }

      if (this.isRouteFolderDetails) {
        params.parentFolderId = Number(this.$route.params.folderId)
      }

      try {
        if (this.numberOfLoadedPresentationPages === 1) {
          this.setPresentationsAction([])
          const action = this.isRouteFolderDetails ? this.getSubFoldersAction(params) : this.getFoldersAction(params)
          await action
        }

        await this.getPresentationsAction(params)
      } catch (error) {
        this.$router.push({ name: 'Presentations' })
        this.setErrorOnInternalServerAction(this.$i18nPresenter.t('Folder not found!'))
        // this.setIsServerConnectableAction(false)
        utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Presentations > Load > Failed')
        throw error
      } finally {
        this.presentationsAndFoldersLoading = false
        this.setMorePresentationsLoadingAction(false)
      }

      // Disable for less noise to event
      // utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Presentations > Load > Succeeded')

      if (this.currentUser.plan === WHITE_LABEL) {
        this.domain = this.currentUser.whiteLabelLink
      }
    },

    async getSharePresentationsAndFolders() {
      // await this.getSharedPresentations()
      if (!this.isServerConnectable) return

      const params = {
        page: this.numberOfLoadedPresentationPages,
        sortOrder: this.selectedSortCriteria.sortOrder,
        sortColumn: this.selectedSortCriteria.sortColumn,
      }

      if (this.searchValue.trim()) {
        params.textSearch = this.searchValue.trim()
      }

      // show loader
      if (this.numberOfLoadedPresentationPages === 1) {
        this.presentationsAndFoldersLoading = true
      } else {
        this.setMorePresentationsLoadingAction(true)
      }

      if (this.isShareRouteFolderDetails) {
        params.parentFolderId = Number(this.$route.params.folderId)
      }

      try {
        if (this.numberOfLoadedPresentationPages === 1) {
          this.setPresentationsAction([])
          const action = this.isShareRouteFolderDetails ?
            this.getShareSubFoldersAction(params) : this.getShareFoldersAction(params)
          await action
        }

        await this.getSharedPresentationsAction(params)
      } catch (error) {
        // this.setIsServerConnectableAction(false)
        utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Shared Presentations > Load > Failed')
        this.$router.push({ name: 'SharedWithMe' })
        this.setErrorOnInternalServerAction(this.$i18nPresenter.t('Folder not found!'))
        throw error
      } finally {
        this.presentationsAndFoldersLoading = false
        this.setMorePresentationsLoadingAction(false)
      }

      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Shared Presentations > Load > Succeeded')

      if (this.currentUser.plan === WHITE_LABEL) {
        this.domain = this.currentUser.whiteLabelLink
      }
    },
    async getTeamPresentationsAndFolders() {
      if (!this.isServerConnectable) return
      const params = {
        page: this.numberOfLoadedPresentationPages,
        sortOrder: this.selectedSortCriteria.sortOrder,
        sortColumn: this.selectedSortCriteria.sortColumn,
        fromWorkspace: true,
      }
      if (this.$store.state.currentAccount && this.$store.state.currentAccount.id) {
        params.accountId = this.$store.state.currentAccount.id
        await this.getTeamFoldersAction(params)
      }
      if (this.selectedFolderId) {
        params.folderId = this.selectedFolderId
      } else {
        params.folderId = ''
      }
      if (this.numberOfLoadedPresentationPages === 1) {
        this.presentationsAndFoldersLoading = true
      } else {
        this.setMorePresentationsLoadingAction(true)
      }

      try {
        if (this.numberOfLoadedPresentationPages === 1) {
          this.setPresentationsAction([])
        }

        await this.getPresentationsAction(params)
      } catch (error) {
        if (error.status === 400) {
          // remove out of team but member state remaining call api, force reload personal mode
          this.$store.state.workingMode = constants.workspace.PERSONAL
          this.setCurrentAccountAction({})
          this.$router.push({ name: 'Presentations' })
        } else {
          this.setIsServerConnectableAction(false)
          utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Presentations > Load > Failed')
          throw error
        }
      } finally {
        this.presentationsAndFoldersLoading = false
        this.setMorePresentationsLoadingAction(false)
      }
      if (this.currentUser.plan === WHITE_LABEL) {
        this.domain = this.currentUser.whiteLabelLink
      }
    },
    async handleMyPresentationsScroll() {
      if (this.presentationsAndFoldersLoading) return

      if (this.morePresentationsLoading) return

      if (this.numberOfLoadedPresentationPages >= this.numberOfPresentationPages) return

      if (!this.$refs.myPresentations) return

      if (window.innerHeight + window.scrollY >= this.$refs.myPresentations.offsetHeight - 100) {
        this.setNumberOfLoadedPresentationPagesAction(this.numberOfLoadedPresentationPages + 1)
        await this._getPresentations()
      }
    },
    clickBreadcrumbsSecondLevel() {
      if (this.$route.name === 'TeamFolderDetail') {
        this.$router.push({ name: 'Team' })
      } else if (this.isShareRouteFolderDetails) {
        if (!this.isEnableAddinPowerpoint) {
          this.$router.push({ name: 'SharedWithMe' })
        } else {
          window.location.href = `${configs.presenterDomain}/apps/shared-presentations`
        }
      } else {
        if (!this.isEnableAddinPowerpoint) {
          this.$router.push({ name: 'Presentations' })
          return
        }
        window.location.href = `${configs.presenterDomain}/apps/presentations`
      }
    },
    clickBreadcrumbsNLevel(folderId) {
      let name = 'FolderDetail'
      if (this.isShareRouteFolderDetails) {
        name = 'ShareFolderDetail'
      }
      if (!this.isEnableAddinPowerpoint) {
        this.$router.push({
          name,
          params: {
            folderId,
          },
        })
        return
      }
      if (name === 'FolderDetail') {
        window.location.href = `${configs.presenterDomain}/apps/presentations/folders/${folderId}`
      } else if (name === 'ShareFolderDetail') {
        window.location.href = `${configs.presenterDomain}/apps/shared-presentations/folders/${folderId}`
      }
    },
    checkToShowEarlyBirdModal() {
      setTimeout(() => {
        if (this.currentAccount &&
          this.currentAccount.createdBy
          && !this.hasOnboardingV3
          && !(this.currentUser.personalInformation && this.currentUser.personalInformation.teamDiscount)
          && !localStorage.getItem(`dont-show-early-bird-modal-again-${this.currentUser.id}`)
          && !sessionStorage.getItem(`isShowEarlyBirdModal-${this.currentUser.id}`)
          && (this.currentAccount.plan === 'free' || !this.currentAccount.plan)
        ) {
          this.isShowEarlyBirdModal = true
          sessionStorage.setItem(`isShowEarlyBirdModal-${this.currentUser.id}`, 'true')
        }
      }, 3000)
    },
    checkToShowTrialModal() {
      setTimeout(() => {
        if (canShowTrialNotAvailablePopup()) {
          this.isShowTrialNotEligibleModal = true
          clearPopupSessionData()
        }

        if (canShowEnjoyTrialPopup()) {
          this.isShowEnjoyTrialModal = true
          clearPopupSessionData()
        }
      }, 1000)
    },

    hideTrialNotAvailableModal() {
      this.isShowTrialNotEligibleModal = false
      this.$router.replace({
        path: '/apps/presentations',
      })
    },
  },
}
</script>
